import { useMemo } from 'react'
import { useFirestoreGeneral, useFirestoreQuery } from '../firebase/useFirestore'
import notDeleted from '../helpers/notDeleted'
import computeHouseholdMaterials from '../helpers/materialsHelpers/computeHouseholdMaterials'
import computeHouseholdImpact from '../helpers/materialsHelpers/computeHouseholdImpact'
import readHouseholdField from '../helpers/adressHelpers/householdFields'
import useInterventieLibrary from './useInterventieLibrary'

// AI-CONTEXT: See docs/patterns.md#hook-firestore-custom (composing variant)

/**
 * Materials attached directly to a household (address). Composes the
 * `AdressMaterials` entries for the address with the master `Materials` list
 * and runs the pure `computeHouseholdMaterials` planner.
 *
 * Materials live on the household (this collection), not on its subtasks —
 * the old subtask-aggregation hook has been retired.
 *
 * Also returns the master `materials` list it already fetches, so consumers
 * (e.g. the add-picker) don't open a second `Materials` listener. That list
 * holds only LIVE materials; the join above also sees soft-deleted ones.
 *
 * Additionally returns the household's CO2 / savings `impact` against the
 * milieu-circulariteit standaard. Computed here rather than in each card so the
 * Overzicht cards, the Materialen tab and any later view share one calculation
 * instead of three copies that drift apart.
 * AI-CONTEXT: docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
 *
 * The extra keys are additive — the three existing consumers keep working
 * unchanged, since an unread key on the returned object strands nobody.
 *
 * @param {string} adressID - Adresses.ID of the household ('' while unknown)
 * @returns {{
 *   rows: Array<object>,
 *   entries: Array<object>,
 *   totals: {used: number, planned: number, combined: number},
 *   loading: boolean,
 *   materials: Array<object>,
 *   allMaterials: Array<object>,
 *   impact: object,
 *   residents: number|null,
 *   impactError: *,
 * }} `materials` = live master list (for the add-picker); `allMaterials` =
 *   the same list INCLUDING soft-deleted materials (for recognising a row's
 *   material), see inbox #284.
 */
const useAdressMaterials = (adressID) => {
    const entries = useFirestoreGeneral('AdressMaterials', 'AdressID', adressID || '')
    // Óók de zacht verwijderde materialen: een regel bij de bewoner wijst er nog
    // naar, en zonder het materiaal wordt hij 'Onbekend materiaal' zonder eenheid
    // en prijs en valt hij uit de CO2 en de besparing (inbox #284). De keuzelijst
    // krijgt hieronder alleen de levende.
    // AI-CONTEXT: See docs/pitfalls.md#database-hard-to-soft-delete-leaves-old-orphans
    const rawMaterials = useFirestoreQuery('Materials', {
        orderBy: ['Position', 'asc'],
        includeDeleted: true,
    })
    // Per-bewoner interventies (ET07 douchekop, ET09 douchetimer) scale with
    // this. Vast gegeven `AantalBewoners` op het huishouden; tot de migratie
    // de oude regel op de Gegevens-tab. AI-CONTEXT: See docs/features/huishouden-vaste-gegevens.md (T8)
    const personaData = useFirestoreGeneral('PersonaData', 'PersonaID', adressID || '')
    const adressList = useFirestoreGeneral('Adresses', 'ID', adressID || '')
    const { library, error: impactError } = useInterventieLibrary()

    const loading = entries === '' || rawMaterials === ''

    // Twee lijsten met een eigen taak: de koppeling met de regels bij de bewoner
    // kent elk materiaal dat ooit bestond, de keuzelijst alleen wat de
    // organisatie nu voert.
    const joinMaterials = useMemo(
        () => (Array.isArray(rawMaterials) ? rawMaterials : []),
        [rawMaterials]
    )
    const materials = useMemo(() => joinMaterials.filter(notDeleted), [joinMaterials])

    const { rows, totals } = useMemo(() => {
        if (loading) {
            return { rows: [], totals: { used: 0, planned: 0, combined: 0 } }
        }
        return computeHouseholdMaterials({
            entries: Array.isArray(entries) ? entries : [],
            materials: joinMaterials,
        })
    }, [entries, joinMaterials, loading])

    const residents = useMemo(
        () => readHouseholdField(
            (Array.isArray(adressList) && adressList[0]) || { ID: adressID },
            Array.isArray(personaData) ? personaData : [],
            'AantalBewoners',
        ),
        [adressList, personaData, adressID]
    )

    // No library means no figures at all — never a zero. A partial or absent
    // standaard makes an unmapped material indistinguishable from one that
    // genuinely saves nothing.
    const impact = useMemo(
        () => computeHouseholdImpact({ rows, materials: joinMaterials, library, residents }),
        [rows, joinMaterials, library, residents]
    )

    // De ruwe regels erbij: het materialenscherm heeft ze nodig om het
    // voorraadeffect te bepalen (Deducted, mee te nemen, gebruikt), dat in een
    // weergaveregel niet zit. AI-CONTEXT: See docs/features/household-materials.md (Uitbreiding 2026-10)
    const rawEntries = useMemo(() => (Array.isArray(entries) ? entries : []), [entries])

    // `allMaterials` óók met de verwijderde: het scherm moet bij een regel kunnen
    // zien dát zijn materiaal verwijderd is, in plaats van het voor een wees te houden.
    return { rows, entries: rawEntries, totals, loading, materials, allMaterials: joinMaterials, impact, residents, impactError }
}

export default useAdressMaterials
